import { useState } from 'react';
import { CheckIcon, ChevronsUpDownIcon, ChevronDownIcon, InfoIcon, XIcon } from 'lucide-react';
import { Button } from './button';
import { Badge } from './badge';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './command';
import { cn } from '../lib/utils';

export type MatchMode = 'or' | 'and';

type FilterMultiSelectProps = {
	values: string[];
	options: { value: string; label: string }[];
	onChange: (values: string[]) => void;
	placeholder: string;
	searchPlaceholder?: string;
	emptyText?: string;
	maxDisplayResults?: number;
	/**
	 * Visual style of the trigger button.
	 * - `outline` (default): dashed border button style (backoffice)
	 * - `pill`: rounded pill style (portals)
	 */
	variant?: 'outline' | 'pill';
	/** Normalize search strings (e.g. remove diacritics). If not provided, uses simple lowercase matching. */
	normalizeString?: (str: string) => string;
	/** When true, shows an OR/AND toggle in the dropdown when 2+ values are selected. */
	showMatchMode?: boolean;
	/** Current match mode. Defaults to 'or'. */
	matchMode?: MatchMode;
	/** Called when the user toggles between OR and AND. */
	onMatchModeChange?: (mode: MatchMode) => void;
};

const pillClassName =
	'border-border bg-background hover:bg-muted inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-xl border px-4 text-sm font-medium transition-colors cursor-pointer';

const pillActiveClassName = 'border-primary bg-primary/5 text-foreground';

export function FilterMultiSelect({
	values,
	options,
	onChange,
	placeholder,
	searchPlaceholder,
	emptyText = 'No results found.',
	maxDisplayResults = 20,
	variant = 'outline',
	normalizeString,
	showMatchMode = false,
	matchMode = 'or',
	onMatchModeChange,
}: FilterMultiSelectProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');

	const normalize = normalizeString ?? ((s: string) => s.toLowerCase());

	const filtered = options
		.filter((option) => {
			if (!search) return true;
			const norm = normalize(search);
			return normalize(option.label).includes(norm) || option.value.includes(norm);
		})
		.slice(0, maxDisplayResults);

	const toggle = (value: string) => {
		onChange(values.includes(value) ? values.filter((v) => v !== value) : [...values, value]);
	};

	const isPill = variant === 'pill';

	return (
		<div className="flex items-center gap-1">
			<Popover open={open} onOpenChange={setOpen}>
				{/* Always `asChild`: the trigger merges onto the single child element (the pill
				    `<button>` or the outline `<Button>`). With `asChild={false}` on the pill path,
				    PopoverTrigger rendered its own `<button>` wrapping the pill `<button>` — illegal
				    `<button>`-in-`<button>` nesting the browser auto-corrects, which diverges from the
				    SSR HTML and triggers a React #418 hydration mismatch on the dashboard (TEC-3481). */}
				<PopoverTrigger asChild>
					{isPill ? (
						<button type="button" className={cn(pillClassName, values.length > 0 && pillActiveClassName)}>
							{placeholder}
							{values.length > 0 && (
								<span className="text-muted-foreground text-xs font-normal">({values.length})</span>
							)}
							<ChevronDownIcon className="text-muted-foreground size-4" />
						</button>
					) : (
						<Button variant="outline" className="border-border w-fit border-dashed font-normal">
							{placeholder}
							{values.length > 0 && (
								<Badge variant="secondary" className="ml-1 rounded-sm px-1.5 font-normal">
									{values.length}
									{showMatchMode && values.length >= 2 ? ` · ${matchMode === 'and' ? 'All' : 'Any'}` : ''}
								</Badge>
							)}
							<ChevronsUpDownIcon className="text-muted-foreground ml-1 size-3.5" />
						</Button>
					)}
				</PopoverTrigger>
				<PopoverContent className="w-64 p-0" align="start">
					<Command shouldFilter={false}>
						{searchPlaceholder && (
							<CommandInput placeholder={searchPlaceholder} value={search} onValueChange={setSearch} />
						)}
						<CommandList>
							<CommandEmpty>{emptyText}</CommandEmpty>
							<CommandGroup>
								{filtered.map((option) => (
									<CommandItem key={option.value} value={option.value} onSelect={() => toggle(option.value)}>
										<div className="border-border flex size-4 shrink-0 items-center justify-center rounded-[4px] border">
											{values.includes(option.value) && <CheckIcon className="size-3" />}
										</div>
										{option.label}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
						{values.length > 0 && (
							<div className="border-border border-t p-1">
								{showMatchMode && values.length >= 2 && onMatchModeChange && (
									<div className="flex items-center justify-between px-2 py-1.5">
										<span className="text-muted-foreground flex items-center gap-1 text-xs">
											Show items with
											<span className="group relative">
												<InfoIcon className="text-muted-foreground/60 hover:text-muted-foreground size-3 cursor-help" />
												<span className="bg-popover text-popover-foreground ring-border pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 w-48 -translate-x-1/2 rounded-md p-2 text-xs leading-relaxed opacity-0 shadow-md ring-1 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
													<strong>Any:</strong> Show items that match at least one of the selected values.
													<br />
													<strong>All:</strong> Show only items that match every selected value.
												</span>
											</span>
										</span>
										<div className="border-border flex rounded-md border text-xs">
											<button
												type="button"
												onClick={() => onMatchModeChange('or')}
												className={cn(
													'px-2 py-0.5 rounded-l-md transition-colors',
													matchMode === 'or'
														? 'bg-foreground text-background'
														: 'text-muted-foreground hover:text-foreground',
												)}
											>
												Any
											</button>
											<button
												type="button"
												onClick={() => onMatchModeChange('and')}
												className={cn(
													'px-2 py-0.5 rounded-r-md transition-colors border-l border-border',
													matchMode === 'and'
														? 'bg-foreground text-background'
														: 'text-muted-foreground hover:text-foreground',
												)}
											>
												All
											</button>
										</div>
									</div>
								)}
								<button
									type="button"
									onClick={() => {
										onChange([]);
										setSearch('');
									}}
									className="text-muted-foreground hover:text-foreground flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm"
								>
									<XIcon className="size-3.5" />
									Clear selection
								</button>
							</div>
						)}
					</Command>
				</PopoverContent>
			</Popover>

			{!isPill && values.length > 0 && (
				<Button
					type="button"
					size="sm"
					variant="outline"
					className="border-dashed"
					onClick={() => onChange([])}
					title="Clear filter"
				>
					<XIcon />
				</Button>
			)}
		</div>
	);
}
